import { messageService } from './services/MessageService';
import { notificationService } from './services/NotificationService';
import type { ToastServiceAPI, NotificationServiceAPI } from './Message.types';

/**
 * API para mostrar toasts (mensajes temporales apilables)
 * Los toasts se apilan hasta 5 y cada uno tiene su propio timer
 *
 * @example
 * toast.success('Operación exitosa')
 * toast.error('Algo salió mal')
 * toast.info('Información')
 * toast.warning('Advertencia')
 */
export const toast: ToastServiceAPI = messageService;

/**
 * API de notificaciones agrupadas: mazos `primary` / `secondary` (explícitos) o mazo por defecto **`auto`**
 * cuando no se indica `stackGroup` (una sola cola para todas las variantes Figma).
 *
 * @example
 * notification.message({ title: 'Novedad', stackGroup: 'primary' })
 * notification.message({ title: 'Recordatorio', stackGroup: 'secondary', appearance: { intent: 'preventiva' } })
 * notification.dismissStack('auto')
 */
export const notification: NotificationServiceAPI = notificationService;

/**
 * API unificada de mensajes
 * Combina funcionalidad de toast y notification
 *
 * @example
 * // Toasts
 * notify.success('Guardado')
 * notify.error('Error')
 *
 * // Sin stackGroup → mazo por defecto `auto` (todas las intenciones / CTA / simples comparten cola)
 * notify.message({ title: 'Novedad' })
 * notify.message({ title: 'Aviso', appearance: { intent: 'critica' } })
 *
 * // Mazo explícito
 * notify.message({ title: 'Recordatorio', stackGroup: 'secondary' })
 */
export const notify = {
  // Métodos de toast (se apilan)
  success: messageService.success.bind(messageService),
  error: messageService.error.bind(messageService),
  info: messageService.info.bind(messageService),
  warning: messageService.warning.bind(messageService),
  show: messageService.show.bind(messageService),
  dismiss: messageService.dismiss.bind(messageService),
  configure: messageService.configure.bind(messageService),

  /** Encola una notificación agrupada; mismas opciones que `notification.message`. */
  message: notificationService.message.bind(notificationService),
  /** Vacía el mazo explícito `primary` o `secondary` (equivale a `notification.dismissGroup`). */
  dismissNotification: notificationService.dismissGroup.bind(notificationService),
  /** Vacía el mazo por `stackKey` (p. ej. `'auto'`). */
  dismissNotificationStack: notificationService.dismissStack.bind(notificationService),
  /** Configura duración, posición, `stackMax`, barra de tiempo, etc., del servicio de notificaciones. */
  configureNotification: notificationService.configure.bind(notificationService),
};
